import { useMemo, useState } from 'react';
import { useRecentCalls } from './useRecentCalls';
import { RecentCallsList } from './RecentCallsList';
import { CallReviewPanel } from './CallReviewPanel';
import { useRedial } from './useRedial';

export function SoftphoneRecents(): JSX.Element {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data, isLoading } = useRecentCalls(15);
  const { redial, busy, canRedial } = useRedial();

  const items = data?.items ?? [];
  const lastCall = items[0] ?? null;

  const { selectedCall, prevCall, nextCall } = useMemo(() => {
    if (!selectedId) return { selectedCall: null, prevCall: null, nextCall: null };
    const idx = items.findIndex((c) => c.id === selectedId);
    if (idx < 0) return { selectedCall: null, prevCall: null, nextCall: null };
    return {
      selectedCall: items[idx] ?? null,
      // Items are ordered most-recent first; "Prev" = older, "Next" = newer.
      prevCall: items[idx + 1] ?? null,
      nextCall: idx > 0 ? items[idx - 1] ?? null : null,
    };
  }, [items, selectedId]);

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">Recent</h3>
        <button
          type="button"
          onClick={() => lastCall && void redial(lastCall)}
          disabled={!lastCall || !canRedial || busy}
          className="btn-secondary !px-2 !py-1 text-xs"
          title="Redial last number"
        >
          ↻ Last
        </button>
      </div>

      {isLoading ? (
        <div className="text-xs text-ink-500 italic px-1 py-2">Loading…</div>
      ) : (
        <RecentCallsList
          calls={items}
          selectedId={selectedId}
          onSelect={setSelectedId}
          compact
        />
      )}

      {selectedCall && (
        <CallReviewPanel
          selectedCall={selectedCall}
          prevCall={prevCall}
          nextCall={nextCall}
          onSelect={setSelectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </section>
  );
}
